import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import moment from 'moment';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { ModelOptionsLoaderService } from '../../services/model-options-loader/model-options-loader.service';
import { ValueHelperInstanceToken } from '../../interfaces/value-helper.interface';
import { TIMERECORDING_TYPE_OF_WORK_CODES } from '../complex/time-recording-entry.model';
import {
    JsonNotificationIdentifierModel,
    NotificationIdentifierModel,
    NotificationIdentifierModelFromJson,
} from '../low-level/notification-identifier.model';
import { DateModelOptions } from '../options/date-model-options';
import { TimeModelOptions } from '../options/time-model-options';
import { DateModel } from '../primitive/date.model';
import { TextModel } from '../primitive/text.model';
import { TimeModel } from '../primitive/time.model';
import { CompositeModel } from './composite.model';
import { DropdownModel } from './dropdown.model';
import {
    JsonTimesheetAddressModel,
    TimesheetAddressModel,
    TimesheetAddressModelFromJson,
} from './timesheet-address.model';
import { getNextTimesheetEntryCounter } from './timesheet-entry-counter';
import { TimesheetEntryInterface } from './timesheet-entry.interface';
import { ODataTimesheetEntryOutModel } from './timesheet-entry.model';

export class TimesheetManualEntryModel extends CompositeModel implements TimesheetEntryInterface {
    public startDate: DateModel;
    public startTime: TimeModel;
    public endDate: DateModel;
    public endTime: TimeModel;
    public address: TimesheetAddressModel | undefined;
    public taskTypeDropdown: DropdownModel<TextModel>;
    public notificationId: NotificationIdentifierModel;
    public distance: TextModel;
    public travelDescription = '';

    public readonly index: number;
    public constructor(
        startDateTime: moment.Moment,
        endDateTime: moment.Moment,
        minStart: moment.Moment,
        maxEnd: moment.Moment,
        address?: TimesheetAddressModel,
        private addressInOutModel = true,
        private readonly isTravelBillable = false,
        notificationIdentifier?: NotificationIdentifierModel,
        public readonly travelType?: TIMERECORDING_TYPE_OF_WORK_CODES,
    ) {
        super('TimesheetManualEntryModel');

        const injector = CustomInjector.getInjector();
        const valueHelperService = injector.get(ValueHelperInstanceToken);
        const modelOptionsLoader = injector.get(ModelOptionsLoaderService);
        const translate = injector.get(TranslateService);

        this.index = getNextTimesheetEntryCounter();

        const tomorrowEndOfDay = moment().add(1, 'day').endOf('day');
        const maxValue = maxEnd.isAfter(tomorrowEndOfDay) ? tomorrowEndOfDay : maxEnd;

        this.startDate = <DateModel>this.set(
            new DateModel(
                'StartDate',
                startDateTime,
                new DateModelOptions({
                    minValue: minStart,
                    maxValue: maxValue,
                    readonly: !!travelType,
                }),
            ),
        );
        this.startTime = <TimeModel>this.set(
            new TimeModel(
                'StartTime',
                startDateTime,
                new TimeModelOptions({
                    minValue: minStart,
                    maxValue: maxValue,
                    readonly: !!travelType,
                }),
            ),
        );
        this.endDate = <DateModel>this.set(
            new DateModel(
                'EndDate',
                endDateTime,
                new DateModelOptions({
                    minValue: minStart,
                    maxValue: maxValue,
                }),
            ),
        );
        this.endTime = <TimeModel>this.set(
            new TimeModel(
                'EndTime',
                endDateTime,
                new TimeModelOptions({
                    minValue: minStart,
                    maxValue: maxValue,
                }),
            ),
        );
        this.address = address;

        // only entries should be shown here that have numeric values for the field "code".
        // Please see https://evorait.atlassian.net/wiki/spaces/SHC/pages/100238380/Time+Recording
        const taskTypesGap = valueHelperService.getTimesheetGapTypeInfo().filter((vh) => vh.code.match(/^[0-9]*$/));
        // If travelType is defined and is TRBK or Standby,
        // then set the task description to travel back type.
        if (travelType === TIMERECORDING_TYPE_OF_WORK_CODES.TRBK) {
            this.travelDescription = <string>translate.instant('TIMESHEET_TRAVEL_BACK');
        } else if (travelType === TIMERECORDING_TYPE_OF_WORK_CODES.TRAVEL_BACK_STANDBY) {
            this.travelDescription = <string>translate.instant('TIMESHEET_TRAVEL_BACK_STANDBY');
        }
        this.taskTypeDropdown = <DropdownModel<TextModel>>this.set(
            new DropdownModel<TextModel>(
                'WorkCode',
                taskTypesGap.map((taskType) => {
                    return new DisplayDecorator<TextModel>(taskType.descText, new TextModel('WorkCode', taskType.code));
                }),
            ),
        );
        modelOptionsLoader.load(this.taskTypeDropdown, 'Timesheet', 'GapTask');

        this.notificationId = notificationIdentifier
            ? notificationIdentifier
            : new NotificationIdentifierModel('', '', '');
    }

    public toJSON(): unknown {
        return {
            startDateTime: this.startDate.value,
            endDateTime: this.endDate.value,
            minStart: this.startDate.options.minValue,
            maxEnd: this.endDate.options.maxValue,
            address: this.address,
            taskType: this.taskTypeDropdown.selectedEntry ? this.taskTypeDropdown.selectedEntry.value : '',
            isTravelBillable: this.isTravelBillable,
            addressInOutModel: this.addressInOutModel,
            notificationId: this.notificationId.qmNum ? this.notificationId : undefined,
            travelType: this.travelType,
        };
    }

    public getOutModel(): ODataTimesheetEntryOutModel {
        const outModel = <ODataTimesheetEntryOutModel>super.getOutModel();

        _.merge(outModel, this.notificationId.getOutModel());
        delete (outModel as Omit<ODataTimesheetEntryOutModel, 'FrontId'>).FrontId;

        if (this.addressInOutModel && this.address) {
            _.merge(outModel, this.address.getOutModel());
        }

        // Add  distance property to outModel if existing
        if (this.distance) {
            _.merge(outModel, this.distance.getOutModel());
        }

        if (this.travelType) {
            outModel.WorkCode =
                this.travelType === TIMERECORDING_TYPE_OF_WORK_CODES.TRBK
                    ? TIMERECORDING_TYPE_OF_WORK_CODES.TRBK
                    : TIMERECORDING_TYPE_OF_WORK_CODES.TRAVEL_BACK_STANDBY;
        }

        return outModel;
    }
}

export class TimesheetManualEntryModelFromJson extends TimesheetManualEntryModel {
    public constructor(data: JsonTimesheetManualEntryModel) {
        super(
            data.startDateTime ? moment(data.startDateTime) : moment.invalid(),
            data.endDateTime ? moment(data.endDateTime) : moment.invalid(),
            data.minStart ? moment(data.minStart) : moment.invalid(),
            data.maxEnd ? moment(data.maxEnd) : moment.invalid(),
            data.address ? new TimesheetAddressModelFromJson(data.address) : undefined,
            data.addressInOutModel,
            data.isTravelBillable,
            data.notificationId ? new NotificationIdentifierModelFromJson(data.notificationId) : undefined,
            data.travelType,
        );

        if (data.taskType) {
            this.taskTypeDropdown.selectByValue(data.taskType);
        }
    }
}

export interface JsonTimesheetManualEntryModel {
    startDateTime: string;
    endDateTime: string;
    minStart: string;
    maxEnd: string;
    address: JsonTimesheetAddressModel;
    addressInOutModel: boolean;
    taskType: string;
    isTravelBillable: boolean;
    notificationId: JsonNotificationIdentifierModel;
    travelType: TIMERECORDING_TYPE_OF_WORK_CODES;
}
